import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { PrinterIcon, ArrowLeftIcon } from "@phosphor-icons/react";

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

export default function AmenityBookingPrint() {
  const { id } = useParams();
  const [booking, setBooking] = useState(null);
  const [amenity, setAmenity] = useState(null);
  const [property, setProperty] = useState(null);
  const [platform, setPlatform] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const props = await api.get("/properties");
        for (const p of props.data || []) {
          const rows = await api.get("/amenities/bookings", { params: { property_id: p.id } }).catch(() => ({ data: [] }));
          const found = (rows.data || []).find((x) => x.id === id);
          if (found) {
            setBooking(found);
            setProperty(p);
            const am = await api.get("/amenities", { params: { property_id: p.id } }).catch(() => ({ data: [] }));
            setAmenity((am.data || []).find((a) => a.id === found.amenity_id));
            break;
          }
        }
      } catch {}
      api.get("/settings/platform/public").then((r) => setPlatform(r.data || {})).catch(() => {});
    })();
  }, [id]);

  if (!booking) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading amenity bill…</div>;

  const L = { ...(platform.invoice_layout || {}) };
  const banner = L.show_header_banner !== false;
  const displayName = (L.header_property_display || "").trim() || property?.name || "—";
  const overline = (L.header_group_overline || "").trim() || platform.group_name || "The King's Grand Group of Hotels";
  const location = (L.header_location_badge || "").trim() || [property?.city, property?.state].filter(Boolean).join(", ");
  const bg = L.header_bg_color || "#2F5D50";
  const accent = L.header_accent_color || "#C8A24B";
  const total = Number(booking.price || 0) + Number(booking.gst_amount || 0);

  return (
    <div className="min-h-screen bg-background text-foreground py-8 print:py-0">
      <div className="max-w-3xl mx-auto px-4 print:px-0 print:max-w-none">
        <div className="flex items-center justify-between gap-4 mb-4 print:hidden">
          <Button variant="outline" size="sm" onClick={() => window.history.back()}><ArrowLeftIcon size={14} className="mr-1" /> Back</Button>
          <Button size="sm" onClick={() => window.print()} data-testid="amenity-print-btn"><PrinterIcon size={14} className="mr-1" /> Print</Button>
        </div>

        <div className="rounded-md overflow-hidden border border-border">
          {banner && (
            <div className="p-6 text-white" style={{ backgroundColor: bg }} data-testid="amenity-print-banner">
              <p className="text-xs uppercase tracking-[0.25em] mb-2" style={{ color: accent }}>◆ {overline}</p>
              <h1 className="font-display text-3xl md:text-4xl leading-tight" style={{ color: accent }}>
                {displayName}
                {location && <span className="ml-3 align-middle inline-block text-[10px] tracking-widest uppercase border rounded-full px-2 py-1" style={{ borderColor: accent, color: "white" }}>{location}</span>}
              </h1>
              <p className="text-sm mt-2 opacity-90">Amenity bill · {booking.booking_ref || booking.id.slice(-8)}</p>
            </div>
          )}
          <div className="p-6 bg-card space-y-6">
            <div className="grid grid-cols-2 gap-6">
              <div>
                <p className="label-overline mb-1">Guest</p>
                <p className="font-medium">{booking.customer_name}</p>
                <p className="text-sm text-muted-foreground">{booking.customer_phone || "—"}</p>
              </div>
              <div>
                <p className="label-overline mb-1">Service</p>
                <p className="font-medium">{amenity?.name || "—"}</p>
                <p className="text-sm text-muted-foreground">Slot: {new Date(booking.slot_at).toLocaleString("en-IN")}</p>
                {booking.notes && <p className="text-xs text-muted-foreground mt-1">{booking.notes}</p>}
              </div>
            </div>

            <div className="ml-auto max-w-sm space-y-1">
              <div className="flex justify-between text-sm"><span>Charge</span><span className="font-mono-plex">{inr(booking.price)}</span></div>
              {booking.gst_amount ? <div className="flex justify-between text-sm"><span>GST</span><span className="font-mono-plex">{inr(booking.gst_amount)}</span></div> : null}
              <div className="flex justify-between pt-2 border-t border-border font-medium">
                <span>Total</span><span className="font-display text-xl" data-testid="amenity-print-total">{inr(total)}</span>
              </div>
              <div className="flex justify-between text-xs pt-1">
                <span>Payment mode</span><span className="uppercase tracking-wider">{booking.payment_mode || "cash"}</span>
              </div>
            </div>

            <div className="pt-6 border-t border-border text-center text-xs text-muted-foreground">
              <p>Thank you for choosing {displayName}.</p>
              <p className="mt-1">This is a computer-generated bill.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
